import { getKeylessIntegrationLabels, INTEGRATION_TYPES } from '../../helpers'
import { KeylessIntegrationConfig } from './integration-card.types'
import {
  AccentraConnectionSchema,
  accentraDefaultValues,
} from './providers/accentra/accentra.schema'
import { AccentraFields } from './providers/accentra/accentra-fields'
import { useAccentraConnection } from './providers/accentra/use-accentra-connection'
import { useAccentraSubmit } from './providers/accentra/use-accentra-submit'
import { brivoDefaultValues, makeBrivoConnectionSchema } from './providers/brivo/brivo.schema'
import { BrivoFields } from './providers/brivo/brivo-fields'
import { useBrivoConnection } from './providers/brivo/use-brivo-connection'
import { useBrivoSubmit } from './providers/brivo/use-brivo-submit'
import {
  ButterflyConnectionSchema,
  butterflyDefaultValues,
} from './providers/butterfly/butterfly.schema'
import { ButterflyFields } from './providers/butterfly/butterfly-fields'
import { useButterflyConnection } from './providers/butterfly/use-butterfly-connection'
import { useButterflySubmit } from './providers/butterfly/use-butterfly-submit'
import {
  DormakabaConnectionSchema,
  dormakabaDefaultValues,
} from './providers/dormakaba/dormakaba.schema'
import { DormakabaFields } from './providers/dormakaba/dormakaba-fields'
import { useDormakabaConnection } from './providers/dormakaba/use-dormakaba-connection'
import { useDormakabaSubmit } from './providers/dormakaba/use-dormakaba-submit'
import {
  IglooWorksConnectionSchema,
  iglooWorksDefaultValues,
} from './providers/igloo-works/igloo-works.schema'
import { IglooWorksFields } from './providers/igloo-works/igloo-works-fields'
import { useIglooWorksConnection } from './providers/igloo-works/use-igloo-works-connection'
import { useIglooWorksSubmit } from './providers/igloo-works/use-igloo-works-submit'
import {
  PointCentralConnectionSchema,
  pointCentralDefaultValues,
} from './providers/point-central/point-central.schema'
import { PointCentralFields } from './providers/point-central/point-central-fields'
import { usePointCentralConnection } from './providers/point-central/use-point-central-connection'
import { usePointCentralSubmit } from './providers/point-central/use-point-central-submit'
import {
  RemoteLockConnectionSchema,
  remoteLockDefaultValues,
} from './providers/remote-lock/remote-lock.schema'
import { RemoteLockFields } from './providers/remote-lock/remote-lock-fields'
import { useRemoteLockConnection } from './providers/remote-lock/use-remote-lock-connection'
import { useRemoteLockSubmit } from './providers/remote-lock/use-remote-lock-submit'
import {
  makeSaltoSpaceConnectionSchema,
  saltoSpaceDefaultValues,
} from './providers/salto-space/salto-space.schema'
import { SaltoSpaceFields } from './providers/salto-space/salto-space-fields'
import { useSaltoSpaceConnection } from './providers/salto-space/use-salto-space-connection'
import { useSaltoSpaceSubmit } from './providers/salto-space/use-salto-space-submit'
import {
  makeSwiftlaneConnectionSchema,
  swiftlaneDefaultValues,
} from './providers/swiftlane/swiftlane.schema'
import { SwiftlaneFields } from './providers/swiftlane/swiftlane-fields'
import { useSwiftlaneConnection } from './providers/swiftlane/use-swiftlane-connection'
import { useSwiftlaneSubmit } from './providers/swiftlane/use-swiftlane-submit'

// Note: adding a new provider here (once Backend confirms its endpoints/fields) is
// primarily this list plus a new providers/<name>/ folder — not a new hand-rolled card.
export const KEYLESS_INTEGRATIONS_REGISTRY: KeylessIntegrationConfig[] = [
  {
    defaultValues: brivoDefaultValues,
    Fields: BrivoFields,
    id: INTEGRATION_TYPES.BRIVO,
    label: (t) => getKeylessIntegrationLabels(t)[INTEGRATION_TYPES.BRIVO],
    // Note: AC requires active integrations stay editable ("If Active: All parameters
    // currently in use are displayed (editable)") — the legacy behavior (locked forever
    // after connect) was preserved during the Foundation-phase refactor but conflicts with
    // that AC, so it's turned off here per product decision.
    lockAfterConnect: false,
    schema: makeBrivoConnectionSchema,
    useConnection: useBrivoConnection,
    useSubmit: useBrivoSubmit,
  },
  {
    defaultValues: saltoSpaceDefaultValues,
    Fields: SaltoSpaceFields,
    id: INTEGRATION_TYPES.SALTO_SPACE,
    label: (t) => getKeylessIntegrationLabels(t)[INTEGRATION_TYPES.SALTO_SPACE],
    lockAfterConnect: false,
    schema: makeSaltoSpaceConnectionSchema,
    useConnection: useSaltoSpaceConnection,
    useSubmit: useSaltoSpaceSubmit,
  },
  {
    defaultValues: swiftlaneDefaultValues,
    Fields: SwiftlaneFields,
    id: INTEGRATION_TYPES.SWIFTLANE,
    label: (t) => getKeylessIntegrationLabels(t)[INTEGRATION_TYPES.SWIFTLANE],
    lockAfterConnect: false,
    schema: makeSwiftlaneConnectionSchema,
    useConnection: useSwiftlaneConnection,
    useSubmit: useSwiftlaneSubmit,
  },
  {
    defaultValues: dormakabaDefaultValues,
    Fields: DormakabaFields,
    id: INTEGRATION_TYPES.DORMAKABA,
    label: (t) => getKeylessIntegrationLabels(t)[INTEGRATION_TYPES.DORMAKABA],
    lockAfterConnect: false,
    // Note: not yet i18n'd — trivial no-op factory to match the shared `schema: (t) =>
    // ZodTypeAny` shape (see integration-card.types.ts).
    schema: () => DormakabaConnectionSchema,
    useConnection: useDormakabaConnection,
    useSubmit: useDormakabaSubmit,
  },
  {
    defaultValues: iglooWorksDefaultValues,
    Fields: IglooWorksFields,
    id: INTEGRATION_TYPES.IGLOO_WORKS,
    label: (t) => getKeylessIntegrationLabels(t)[INTEGRATION_TYPES.IGLOO_WORKS],
    lockAfterConnect: false,
    // Note: not yet i18n'd — trivial no-op factory, see Dormakaba above.
    schema: () => IglooWorksConnectionSchema,
    useConnection: useIglooWorksConnection,
    useSubmit: useIglooWorksSubmit,
  },
  {
    defaultValues: accentraDefaultValues,
    Fields: AccentraFields,
    id: INTEGRATION_TYPES.ACCENTRA,
    label: (t) => getKeylessIntegrationLabels(t)[INTEGRATION_TYPES.ACCENTRA],
    lockAfterConnect: false,
    // Note: not yet i18n'd — trivial no-op factory, see Dormakaba above.
    schema: () => AccentraConnectionSchema,
    useConnection: useAccentraConnection,
    useSubmit: useAccentraSubmit,
  },
  {
    defaultValues: butterflyDefaultValues,
    Fields: ButterflyFields,
    id: INTEGRATION_TYPES.BUTTERFLY,
    label: (t) => getKeylessIntegrationLabels(t)[INTEGRATION_TYPES.BUTTERFLY],
    lockAfterConnect: false,
    // Note: not yet i18n'd — trivial no-op factory, see Dormakaba above.
    schema: () => ButterflyConnectionSchema,
    useConnection: useButterflyConnection,
    useSubmit: useButterflySubmit,
  },
  {
    defaultValues: pointCentralDefaultValues,
    Fields: PointCentralFields,
    id: INTEGRATION_TYPES.POINT_CENTRAL,
    label: (t) => getKeylessIntegrationLabels(t)[INTEGRATION_TYPES.POINT_CENTRAL],
    lockAfterConnect: false,
    // Note: not yet i18n'd — trivial no-op factory, see Dormakaba above.
    schema: () => PointCentralConnectionSchema,
    useConnection: usePointCentralConnection,
    useSubmit: usePointCentralSubmit,
  },
  {
    defaultValues: remoteLockDefaultValues,
    Fields: RemoteLockFields,
    id: INTEGRATION_TYPES.REMOTE_LOCK,
    label: (t) => getKeylessIntegrationLabels(t)[INTEGRATION_TYPES.REMOTE_LOCK],
    lockAfterConnect: false,
    // Note: not yet i18n'd — trivial no-op factory, see Dormakaba above.
    schema: () => RemoteLockConnectionSchema,
    useConnection: useRemoteLockConnection,
    useSubmit: useRemoteLockSubmit,
  },
]
